import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { EntityTax, PutEntityTaxRequest, TinType } from "@/api/types";
import { TIN_TYPES } from "@/lib/vendorTax";
import { entityTaxLabel, entityTaxState, refreshEntityTaxes, retryEntityTaxes, updateEntityTax, useEntityTaxStore } from "@/lib/entityTaxStore";
import { DialogShell } from "./DialogShell";
import "./VendorTaxDialog.css";
import "./EntityTaxControl.css";

// @ui-check: on a failed load the group shows the alert only - no status words, no mask, no Edit button (never a stale or invented status)
export function EntityTaxControl({ entityId, entityName }: { entityId: string; entityName: string }) {
  const store = useEntityTaxStore();
  const tax = store.rows.get(entityId);
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  function close(): void {
    setOpen(false);
    queueMicrotask(() => trigger.current?.focus());
  }

  const asked = useRef(false);
  const unknown = store.state === "ready" && !tax;
  useEffect(() => {
    if (unknown && !asked.current) {
      asked.current = true;
      refreshEntityTaxes();
    }
  }, [unknown]);

  const state = store.state === "error" && !tax ? "error" : tax ? entityTaxState(tax) : "loading";
  return (
    <>
      <div className="entity-tax-control" role="group" aria-label="Tax identity" data-state={state} aria-busy={state === "loading" ? true : undefined}>
        {state === "error" ? (
          <>
            <span role="alert">{store.error ?? "Could not load tax identity."}</span>
            <button type="button" className="btn btn--ghost" onClick={retryEntityTaxes}>Retry tax identity</button>
          </>
        ) : tax ? (
          <>
            <span>{entityTaxLabel(tax)}{tax.tin.masked ? ` · ${tax.tin.masked}` : ""}</span>
            <button ref={trigger} type="button" className="btn btn--ghost" onClick={() => setOpen(true)}>Edit tax identity</button>
          </>
        ) : (
          <span>Loading tax identity...</span>
        )}
      </div>
      {open && tax && <EntityTaxDialog entityName={entityName} initialTax={tax} onClose={close} />}
    </>
  );
}

const fields = [
  ["addressLine1", "Address line 1"], ["addressLine2", "Address line 2"], ["city", "City"], ["addressState", "State"], ["zip", "ZIP"],
] as const;

function profileOf(tax: EntityTax): Record<(typeof fields)[number][0], string> {
  return {
    addressLine1: tax.addressLine1 ?? "", addressLine2: tax.addressLine2 ?? "", city: tax.city ?? "",
    addressState: tax.addressState ?? "", zip: tax.zip ?? "",
  };
}
const orNull = (v: string): string | null => (v.trim() === "" ? null : v.trim());

function EntityTaxDialog({ entityName, initialTax, onClose }: { entityName: string; initialTax: EntityTax; onClose: () => void }) {
  const [tax, setTax] = useState(initialTax);
  const [profile, setProfile] = useState(() => profileOf(initialTax));
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"none" | "on-file" | "replacing">(initialTax.tin.hasTin ? "on-file" : "none");
  const [tinType, setTinType] = useState<TinType>((initialTax.tin.tinType as TinType | null) ?? "ein");
  const [typedTin, setTypedTin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [tinError, setTinError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const abort = useRef<AbortController | null>(null);
  const id = useId();
  const formId = `${id}-form`;
  const entityId = initialTax.entityId;

  useEffect(() => {
    const request = new AbortController();
    abort.current = request;
    // No draft survives unmount: abort pending requests; component state is discarded.
    return () => { request.abort(); };
  }, []);

  function close(): void {
    setTypedTin("");
    onClose();
  }
  function remember(value: EntityTax): void {
    setTax(value);
    updateEntityTax(value);
  }
  function failedMessage(err: unknown, fallback: string): string {
    return err instanceof ApiError && err.status === 0 ? "Not saved. No connection; enter the tax ID again when you are online." : problemMessage(err, fallback);
  }

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    // @ui-check: the typed number is cleared from state the moment save starts; it lives only in this request's stack frame
    let pendingTin = typedTin;
    setTypedTin("");
    setError(null);
    setTinError(null);
    setFieldErrors(null);
    if (!navigator.onLine) {
      pendingTin = "";
      setError("Not saved. No connection; enter the tax ID again when you are online.");
      return;
    }
    setBusy(true);
    let writingTin = false;
    const signal = abort.current!.signal;
    try {
      const body: PutEntityTaxRequest = {
        addressLine1: orNull(profile.addressLine1), addressLine2: orNull(profile.addressLine2), city: orNull(profile.city),
        addressState: orNull(profile.addressState), zip: orNull(profile.zip),
      };
      remember(await demoApi.putEntityTax(entityId, body, signal));
      if (signal.aborted) return;
      if (pendingTin.trim() !== "") {
        writingTin = true;
        remember(await demoApi.putEntityTaxId(entityId, { tinType, tin: pendingTin }, signal));
        if (signal.aborted) return;
      }
      close();
    } catch (err: unknown) {
      if (signal.aborted) return;
      const problems = problemFieldErrors(err);
      if (writingTin) setTinError(problems?.tin?.[0] ?? problems?.tinType?.[0] ?? failedMessage(err, "Tax ID not saved."));
      else {
        setFieldErrors(problems);
        setError(failedMessage(err, "Tax identity not saved."));
      }
    } finally {
      pendingTin = "";
      if (!signal.aborted) setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setRemoveError(null);
    setTypedTin("");
    const signal = abort.current!.signal;
    try {
      const value = await demoApi.deleteEntityTaxId(entityId, signal);
      if (signal.aborted) return;
      remember(value);
      setMode("none");
      setTinError(null);
      setConfirmRemove(false);
    } catch (err: unknown) {
      if (!signal.aborted) setRemoveError(failedMessage(err, "Tax ID was not removed."));
    } finally { if (!signal.aborted) setBusy(false); }
  }

  return (
    <>
      <DialogShell title={`Tax identity - ${entityName}`} size="sm" onClose={close} busy={busy || confirmRemove} restoreFocus
        footer={<>
          <button type="button" className="btn btn--ghost" disabled={busy || confirmRemove} onClick={close}>Cancel</button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={busy || confirmRemove}>Save tax identity</button>
        </>}>
        <form id={formId} className="vendor-tax-dialog" onSubmit={(event) => void save(event)} noValidate>
          {fields.map(([field, label]) => <div className="vendor-tax-dialog__field" key={field}>
            <label htmlFor={`${id}-${field}`}>{label}</label>
            <input id={`${id}-${field}`} value={profile[field]} disabled={busy}
              aria-invalid={fieldErrors?.[field] ? true : undefined}
              onChange={(event) => setProfile((current) => ({ ...current, [field]: event.target.value }))} />
          </div>)}
          {error && <p className="vendor-tax-dialog__error" role="alert">{error}</p>}
          <div className="vendor-tax-dialog__tin" role="group" aria-label="Tax ID" data-state={mode}>
            {mode === "on-file" ? <>
              <p>Tax ID on file: {tax.tin.masked}</p>
              <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setTypedTin(""); setTinError(null); setMode("replacing"); }}>Replace tax ID</button>
              <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setRemoveError(null); setConfirmRemove(true); }}>Remove tax ID</button>
            </> : <>
              <div className="vendor-tax-dialog__field">
                <label htmlFor={`${id}-type`}>Tax ID type</label>
                <select id={`${id}-type`} value={tinType} disabled={busy} onChange={(event) => setTinType(event.target.value as TinType)}>
                  {TIN_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>
              <div className="vendor-tax-dialog__field">
                <label htmlFor={`${id}-tin`}>Tax ID</label>
                <input id={`${id}-tin`} type="password" autoComplete="off" inputMode="numeric" value={typedTin}
                  disabled={busy} aria-invalid={tinError ? true : undefined} aria-describedby={tinError ? `${id}-tin-error` : undefined}
                  onChange={(event) => { setTypedTin(event.target.value); setTinError(null); }} />
              </div>
              {mode === "replacing" && <button type="button" className="btn btn--ghost" disabled={busy}
                onClick={() => { setTypedTin(""); setTinError(null); setMode("on-file"); }}>Keep current tax ID</button>}
            </>}
            {tinError && <p id={`${id}-tin-error`} className="vendor-tax-dialog__error" role="alert">{tinError}</p>}
          </div>
        </form>
      </DialogShell>
      {confirmRemove && <DialogShell title="Remove tax ID?" role="alertdialog" size="sm" busy={busy}
        initialFocus={keep} describedBy={`${id}-remove-description`} restoreFocus onClose={() => setConfirmRemove(false)} footer={<>
          <button ref={keep} type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirmRemove(false)}>Keep it</button>
          <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void remove()}>Yes, remove</button>
        </>}>
        <p id={`${id}-remove-description`}>Remove the tax ID on file for {entityName}?</p>
        {removeError && <p className="vendor-tax-dialog__error" role="alert">{removeError}</p>}
      </DialogShell>}
    </>
  );
}
